import { makeAutoObservable, runInAction } from 'mobx'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { matchStudentFromTranscript } from 'shared/utils/matchStudentFromTranscript'
import {
	createSessionNote,
	createSessionActionItem,
	deleteSessionNote,
	deleteSessionActionItem,
	getSessionNote,
	getLatestAiJob,
	listSessionNotesForStudent,
	getSessionTranscript,
	retryAiJob,
	updateSessionActionItem,
	updateSessionSummary,
	uploadSessionAudio,
} from 'api/noteTakerSessionApi'
import { createNoteTakerMediaRecorder } from 'widgets/NoteTaker/services/noteTakerMediaRecorder'
import { buildTranscriptPreview } from 'widgets/NoteTaker/utils/buildTranscriptPreview'
import { buildSessionNotePayload } from 'widgets/NoteTaker/utils/buildSessionNotePayload'
import { buildFullTranscriptText } from 'widgets/NoteTaker/utils/buildFullTranscriptText'
import { mapSessionNoteToDrawerRecord } from 'widgets/NoteTaker/utils/mapSessionNoteToDrawerRecord'
import { AI_PROCESSING_STATUS } from 'widgets/NoteTaker/types/aiProcessing'
import {
	ACTION_ITEM_DEBOUNCE_MS,
	SESSION_DRAWER_STATUS,
	SUMMARY_MODE,
	TRANSCRIPT_DRAWER_STATUS,
} from 'widgets/NoteTaker/types/sessionDrawer'
import { SESSION_CREATION_STATUS } from 'widgets/NoteTaker/types/sessionCreation'
import {
	buildActionItemCreatePayload,
	clonePersistedActionItem,
	mapActionItemFromApi,
	mapOwnerTypeToApi,
} from 'widgets/NoteTaker/utils/sessionActionItems'
import {
	TRANSCRIPT_STATUS,
	createRecordingId,
} from 'widgets/NoteTaker/types/transcriptSegment'

const DEFAULT_GENERAL_NOTES = () => [
	{
		id: createItemId('general'),
		text: "Student is leaning toward UCLA because of program flexibility and CS ranking, but parents remain concerned about the cost difference. UCSD's financial aid is roughly $8k stronger per year and needs a clean comparison before the next session.",
		type: 'note',
		kodaState: 'idle',
	},
]

const DEFAULT_ACTION_ITEMS = () => [
	{
		id: createItemId('action'),
		text: 'Send cost comparison template to the family.',
		kodaState: 'idle',
		ownerType: 'student',
	},
	{
		id: createItemId('action'),
		text: 'Research UCLA vs UCSD scholarship comparison and send to parents.',
		kodaState: 'idle',
		ownerType: 'you',
	},
	{
		id: createItemId('action'),
		text: 'Book follow-up session for cost review discussion.',
		kodaState: 'idle',
		ownerType: 'you',
	},
]

let itemSeed = 0

function createItemId(prefix) {
	itemSeed += 1
	return `${prefix}-${itemSeed}`
}

function normalizeId(value) {
	return String(value || '').trim()
}

function padTime(value) {
	return String(value).padStart(2, '0')
}

function formatDuration(totalSeconds) {
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return `${padTime(minutes)}:${padTime(seconds)}`
}

function cloneItems(items = []) {
	return items.map(item => ({
		...item,
		id: item.id || createItemId('item'),
	}))
}

class NoteTakerStore {
	status = 'idle'
	widgetMode = 'expanded'
	recordingSeconds = 0
	pauseCount = 0
	detectedStudentId = ''
	selectedStudentId = ''
	studentSelectionLocked = false
	lastMatchedTranscript = ''
	unmatchedSyncCount = 0
	assignStudentModalMode = null
	isAssignStudentOpen = false
	isCancelConfirmOpen = false
	isDrawerOpen = false
	showAddedToast = false
	activeNoteRecord = null
	activeNoteStudentId = ''
	kodaPanel = null
	newGeneralNoteText = ''
	newActionItemText = ''
	notesByStudentId = new Map()
	sessionNotesListByStudentId = new Map()
	liveTranscript = ''
	transcriptSegments = []
	recordingTranscriptSegments = []
	recordingId = ''
	transcriptStatus = TRANSCRIPT_STATUS.IDLE
	transcriptSttInFlight = 0
	transcriptError = null
	recorderError = null
	sessionAudioBlob = null
	sessionAudioMimeType = ''
	sessionAudioDurationMs = 0
	recordedAt = ''
	sessionCreationStatus = SESSION_CREATION_STATUS.IDLE
	sessionNoteId = ''
	sessionCreationError = null
	aiProcessingStatus = AI_PROCESSING_STATUS.IDLE
	aiJobId = ''
	aiProcessingError = null
	_sessionCreationAbortController = null
	_aiPollAbortController = null
	_recorder = null
	_transcriptProvider = null
	sessionDrawerStatus = SESSION_DRAWER_STATUS.IDLE
	sessionDrawerError = null
	activeSessionNoteId = ''
	sessionNote = null
	summaryMode = SUMMARY_MODE.VIEW
	summaryDraft = ''
	summarySaved = ''
	summarySaveStatus = 'idle'
	summarySaveError = null
	actionItemsPersisted = []
	actionItemDraft = null
	isTranscriptDrawerOpen = false
	transcriptDrawerStatus = TRANSCRIPT_DRAWER_STATUS.CLOSED
	transcriptDrawerError = null
	transcriptCache = null
	sessionDrawerConflict = false
	isDiscardSummaryPromptOpen = false
	_actionItemDebounceTimers = {}
	_persistedActionItemsSaved = []

	constructor() {
		makeAutoObservable(
			this,
			{
				_recorder: false,
				_transcriptProvider: false,
				_sessionCreationAbortController: false,
				_aiPollAbortController: false,
				_actionItemDebounceTimers: false,
				_persistedActionItemsSaved: false,
			},
			{ autoBind: true },
		)
	}

	get isRecording() {
		return this.status === 'recording'
	}

	get isPaused() {
		return this.status === 'paused'
	}

	get isProcessing() {
		return this.status === 'processing'
	}

	get isStarting() {
		return this.status === 'starting'
	}

	get shouldShowWidget() {
		return ['recording', 'paused', 'processing'].includes(this.status)
	}

	get isSubmittingSession() {
		return [
			SESSION_CREATION_STATUS.DRAINING_TRANSCRIPT,
			SESSION_CREATION_STATUS.CREATING_NOTE,
			SESSION_CREATION_STATUS.UPLOADING_AUDIO,
		].includes(this.sessionCreationStatus)
	}

	get isSessionCreationFailed() {
		return this.sessionCreationStatus === SESSION_CREATION_STATUS.FAILED
	}

	get isSessionCreationComplete() {
		return this.sessionCreationStatus === SESSION_CREATION_STATUS.COMPLETED
	}

	get isSavingSession() {
		return [
			SESSION_CREATION_STATUS.CREATING_NOTE,
			SESSION_CREATION_STATUS.UPLOADING_AUDIO,
		].includes(this.sessionCreationStatus)
	}

	get isAiProcessingFailed() {
		return this.aiProcessingStatus === AI_PROCESSING_STATUS.FAILED
	}

	get isAiProcessingComplete() {
		return this.aiProcessingStatus === AI_PROCESSING_STATUS.COMPLETED
	}

	get isAiProcessingActive() {
		return [
			AI_PROCESSING_STATUS.POLLING,
			AI_PROCESSING_STATUS.QUEUED,
			AI_PROCESSING_STATUS.PROCESSING,
		].includes(this.aiProcessingStatus)
	}

	get isFinalizingSession() {
		return (
			this.isProcessing &&
			!this.isSubmittingSession &&
			!this.isSessionCreationFailed &&
			!this.isAiProcessingActive &&
			!this.isAiProcessingFailed
		)
	}

	get shouldPollAiProcessing() {
		return (
			this.isSessionCreationComplete &&
			this.isProcessing &&
			!this.isAiProcessingFailed &&
			!this.isAiProcessingComplete &&
			Boolean(normalizeId(this.sessionNoteId))
		)
	}

	get finalTranscriptPreview() {
		const fromSegments = buildTranscriptPreview(this.recordingTranscriptSegments)
		if (fromSegments) return fromSegments
		return this.transcriptPreview
	}

	get recordingLabel() {
		return formatDuration(this.recordingSeconds)
	}

	get noteDurationLabel() {
		const hours = Math.floor(this.recordingSeconds / 3600)
		const minutes = Math.floor((this.recordingSeconds % 3600) / 60)
		const seconds = this.recordingSeconds % 60
		if (hours > 0) return `${padTime(hours)}h ${padTime(minutes)}m`
		return `${padTime(minutes)}m ${padTime(seconds)}s`
	}

	get transcriptPreview() {
		const fromSegments = buildTranscriptPreview(this.transcriptSegments)
		if (fromSegments) return fromSegments
		// liveTranscript is mock-only; never fall back to it during real STT.
		if (process.env.REACT_APP_NOTE_TAKER_MOCK_TRANSCRIPT === 'true') {
			return String(this.liveTranscript || '').trim()
		}
		return ''
	}

	get assignedStudentId() {
		return this.selectedStudentId || this.detectedStudentId || this.activeNoteStudentId
	}

	get assignedStudent() {
		const studentId = this.assignedStudentId
		return studentId ? studentsStore.getStudentById(studentId) : null
	}

	get isStudentManuallySelected() {
		return Boolean(this.selectedStudentId)
	}

	get currentGeneralNotes() {
		return this.activeNoteRecord?.generalNotes || []
	}

	get currentActionItems() {
		return this.actionItemsPersisted.length > 0
			? this.actionItemsPersisted
			: this.activeNoteRecord?.actionItems || []
	}

	get isSummaryDirty() {
		return this.summaryMode === SUMMARY_MODE.EDIT && this.summaryDraft !== this.summarySaved
	}

	get hasUnsavedSummary() {
		return this.isSummaryDirty
	}

	get hasActionItemDraft() {
		return Boolean(this.actionItemDraft)
	}

	get hasPendingItemPatches() {
		return (
			this.actionItemsPersisted.some(item => item.saveStatus === 'saving') ||
			Object.keys(this._actionItemDebounceTimers).length > 0
		)
	}

	get isSessionDrawerReady() {
		return this.sessionDrawerStatus === SESSION_DRAWER_STATUS.READY
	}

	startRecording() {
		return this._startRecording()
	}

	async _startRecording() {
		if (this.status !== 'idle') return

		this.recorderError = null
		this.clearSessionCapture()
		this.clearSessionCreationState()
		this.clearAiProcessingState()
		this.widgetMode = 'expanded'
		this.recordingSeconds = 0
		this.pauseCount = 0
		this.detectedStudentId = ''
		this.selectedStudentId = ''
		this.studentSelectionLocked = false
		this.lastMatchedTranscript = ''
		this.unmatchedSyncCount = 0
		this.assignStudentModalMode = null
		this.isAssignStudentOpen = false
		this.isCancelConfirmOpen = false
		this.kodaPanel = null
		this.activeNoteRecord = null
		this.activeNoteStudentId = ''
		this.isDrawerOpen = false
		this.showAddedToast = false
		this.newGeneralNoteText = ''
		this.newActionItemText = ''
		this.clearTranscript()
		this.clearRecordingTranscript()
		this.recordingId = createRecordingId()
		this.recordedAt = new Date().toISOString()
		this._transcriptProvider?.startRecording?.(this.recordingId)
		this.setTranscriptStatus(TRANSCRIPT_STATUS.STREAMING)

		this.status = 'starting'

		try {
			this._recorder = createNoteTakerMediaRecorder({
				onChunk: payload => this._transcriptProvider?.handleChunk?.(payload),
				onWindow: payload => this._transcriptProvider?.handleWindow?.(payload),
				onStop: ({ blob, mimeType, durationMs }) => {
					this.setSessionCapture({ blob, mimeType, durationMs })
					this._transcriptProvider?.handleStop?.({ durationMs })
				},
				onError: error => this.handleRecorderError(error),
			})
			await this._recorder.start({
				timesliceMs: 3000,
			})
			runInAction(() => {
				this.status = 'recording'
			})
		} catch (_) {
			runInAction(() => {
				this._disposeRecorder({ suppressCallbacks: true })
				if (this.status === 'starting') {
					this.status = 'idle'
				}
			})
		}
	}

	tickRecording() {
		if (this.status !== 'recording') return
		this.recordingSeconds += 1
	}

	setLiveTranscript(value) {
		this.liveTranscript = String(value || '')
	}

	getTranscriptSegment(chunkId) {
		const id = Number(chunkId)
		return this.transcriptSegments.find(segment => segment.chunkId === id) || null
	}

	upsertTranscriptSegment(segment) {
		if (!segment || !Number.isFinite(Number(segment.chunkId))) return

		const chunkId = Number(segment.chunkId)
		runInAction(() => {
			const index = this.transcriptSegments.findIndex(item => item.chunkId === chunkId)

			if (index >= 0) {
				this.transcriptSegments[index] = {
					...this.transcriptSegments[index],
					...segment,
					chunkId,
				}
			} else {
				this.transcriptSegments.push({ ...segment, chunkId })
				this.transcriptSegments.sort((a, b) => a.chunkId - b.chunkId)
			}
		})
	}

	setTranscriptStatus(status) {
		this.transcriptStatus = status
	}

	setTranscriptError(error) {
		this.transcriptError = error
			? {
					code: String(error.code || 'TranscriptError'),
					message: String(error.message || 'Transcription failed'),
					...(error.chunkId != null ? { chunkId: error.chunkId } : {}),
			  }
			: null
	}

	beginTranscriptStt() {
		this.transcriptSttInFlight += 1
	}

	endTranscriptStt() {
		this.transcriptSttInFlight = Math.max(0, this.transcriptSttInFlight - 1)
	}

	clearTranscript() {
		this.liveTranscript = ''
		this.transcriptSegments = []
		this.transcriptSttInFlight = 0
		this.transcriptStatus = TRANSCRIPT_STATUS.IDLE
		this.transcriptError = null
	}

	clearRecordingTranscript() {
		this.transcriptSegments = []
		this.recordingTranscriptSegments = []
		this.recordingId = ''
		this.transcriptStatus = TRANSCRIPT_STATUS.IDLE
		this.transcriptError = null
	}

	finalizeRecordingTranscript() {
		this.recordingTranscriptSegments = this.transcriptSegments.map(segment => ({
			...segment,
		}))
		this.transcriptSegments = []
	}

	registerTranscriptProvider(provider) {
		this._transcriptProvider = provider
	}

	registerMockTranscriptProvider(provider) {
		this.registerTranscriptProvider(provider)
	}

	setSessionCreationStatus(status) {
		this.sessionCreationStatus = status
	}

	setSessionCreationError(error) {
		this.sessionCreationError = error
			? {
					code: String(error.code || error.name || 'SessionCreationError'),
					message: String(error.message || 'Failed to save session'),
					...(error.step ? { step: error.step } : {}),
			  }
			: null
	}

	clearSessionCreationState() {
		this._sessionCreationAbortController?.abort()
		this._sessionCreationAbortController = null
		this.sessionCreationStatus = SESSION_CREATION_STATUS.IDLE
		this.sessionNoteId = ''
		this.sessionCreationError = null
	}

	clearAiProcessingState() {
		this._abortAiPoll()
		this.aiProcessingStatus = AI_PROCESSING_STATUS.IDLE
		this.aiJobId = ''
		this.aiProcessingError = null
	}

	_abortAiPoll() {
		this._aiPollAbortController?.abort()
		this._aiPollAbortController = null
	}

	setAiProcessingStatus(status) {
		this.aiProcessingStatus = status
	}

	failAiProcessing(error) {
		this.aiProcessingStatus = AI_PROCESSING_STATUS.FAILED
		this.setAiProcessingError(error)
	}

	setAiProcessingError(error) {
		this.aiProcessingError = error
			? {
					code: String(error.code || error.name || 'AiProcessingError'),
					message: String(error.message || 'AI processing failed'),
			  }
			: null
	}

	applyAiJobPollResult(job) {
		if (!job) return

		const jobId = normalizeId(job.jobId)
		if (jobId) {
			this.aiJobId = jobId
		}

		const status = String(job.status || '')
		if (status === 'queued') {
			this.aiProcessingStatus = AI_PROCESSING_STATUS.QUEUED
			this.setAiProcessingError(null)
			return
		}

		if (status === 'processing') {
			this.aiProcessingStatus = AI_PROCESSING_STATUS.PROCESSING
			this.setAiProcessingError(null)
			return
		}

		if (status === 'failed') {
			this.aiProcessingStatus = AI_PROCESSING_STATUS.FAILED
			this.setAiProcessingError(job.error || {
				code: 'AiProcessingError',
				message: 'AI processing failed',
			})
			return
		}

		if (status === 'completed') {
			this.aiProcessingStatus = AI_PROCESSING_STATUS.COMPLETED
			this.setAiProcessingError(null)
		}
	}

	beginAiProcessingPoll() {
		if (!this.shouldPollAiProcessing) return
		if (this.aiProcessingStatus === AI_PROCESSING_STATUS.IDLE) {
			this.aiProcessingStatus = AI_PROCESSING_STATUS.POLLING
		}
	}

	async finishAiProcessing(sessionNote) {
		if (!sessionNote) {
			this.aiProcessingStatus = AI_PROCESSING_STATUS.FAILED
			this.setAiProcessingError({
				code: 'AiProcessingError',
				message: 'AI processing finished without a session note',
			})
			return
		}

		const studentId = normalizeId(
			this.activeNoteStudentId || this.assignedStudentId || sessionNote.studentId,
		)
		if (studentId && !this.activeNoteStudentId) {
			this.activeNoteStudentId = studentId
		}

		let transcriptText = this.finalTranscriptPreview
		try {
			const transcript = await getSessionTranscript(this.sessionNoteId)
			transcriptText = String(transcript?.transcriptText || transcriptText || '')
		} catch {
			// Keep local finalized transcript when the transcript endpoint is unavailable.
		}

		runInAction(() => {
			this.hydrateSessionDrawer(sessionNote, {
				recordingSeconds: this.recordingSeconds,
				pauseCount: this.pauseCount,
				studentId,
				transcriptText,
			})
			this.activeNoteRecord = mapSessionNoteToDrawerRecord(sessionNote, {
				recordingSeconds: this.recordingSeconds,
				pauseCount: this.pauseCount,
				studentId,
				transcriptText,
			})
			this.aiProcessingStatus = AI_PROCESSING_STATUS.COMPLETED
			this.completeProcessing()
		})

		if (studentId) {
			this.loadSessionNotesForStudent(studentId).catch(() => {})
		}
	}

	async retryAiProcessing() {
		const sessionNoteId = normalizeId(this.sessionNoteId)
		const jobId = normalizeId(this.aiJobId)
		if (!sessionNoteId || !jobId || !this.isAiProcessingFailed) return

		this.setAiProcessingError(null)
		this.aiProcessingStatus = AI_PROCESSING_STATUS.POLLING

		try {
			const job = await retryAiJob(sessionNoteId, jobId)

			// The prior job already finished successfully — recover its result
			// instead of dead-ending, since the backend refuses to re-run it.
			if (String(job?.status || '') === AI_PROCESSING_STATUS.COMPLETED) {
				await this.recoverCompletedAiProcessing(sessionNoteId)
				return
			}

			runInAction(() => {
				this.applyAiJobPollResult(job)
			})
		} catch (cause) {
			// 409 = the job is already completed on the backend; load and finish it.
			if (cause?.response?.status === 409) {
				await this.recoverCompletedAiProcessing(sessionNoteId)
				return
			}

			runInAction(() => {
				this.aiProcessingStatus = AI_PROCESSING_STATUS.FAILED
				this.setAiProcessingError({
					code: cause?.name || 'AiProcessingError',
					message: cause?.message || 'Failed to retry AI processing',
				})
			})
		}
	}

	async recoverCompletedAiProcessing(sessionNoteId) {
		try {
			const sessionNote = await getSessionNote(sessionNoteId)
			await this.finishAiProcessing(sessionNote)
		} catch (cause) {
			runInAction(() => {
				this.aiProcessingStatus = AI_PROCESSING_STATUS.FAILED
				this.setAiProcessingError({
					code: cause?.name || 'AiProcessingError',
					message: cause?.message || 'Failed to load the completed session note',
				})
			})
		}
	}

	discardFailedAiProcessing() {
		this.resetSessionState()
	}

	_abortSessionCreation() {
		this._sessionCreationAbortController?.abort()
		this._sessionCreationAbortController = null
	}

	setSessionCapture({ blob, mimeType, durationMs }) {
		this.sessionAudioBlob = blob || null
		this.sessionAudioMimeType = String(mimeType || '')
		this.sessionAudioDurationMs = Number(durationMs) || 0
	}

	clearSessionCapture() {
		this.sessionAudioBlob = null
		this.sessionAudioMimeType = ''
		this.sessionAudioDurationMs = 0
		this.recordingTranscriptSegments = []
	}

	handleRecorderError({ code, message }) {
		this.recorderError = {
			code: String(code || 'RecorderError'),
			message: String(message || 'Recording failed'),
		}
	}

	_disposeRecorder(options = {}) {
		this._recorder?.dispose(options)
		this._recorder = null
	}

	detectStudent(studentId) {
		const normalizedId = normalizeId(studentId)
		if (!normalizedId) return
		this.detectedStudentId = normalizedId
	}

	assignStudent(studentId) {
		const normalizedId = normalizeId(studentId)
		if (!normalizedId) return
		this.selectedStudentId = normalizedId
		this.activeNoteStudentId = normalizedId
		this.studentSelectionLocked = true
	}

	syncDetectedStudentFromTranscript(studentsOrIndex = [], options = {}) {
		const { forceFuzzy = false } = options
		if (this.studentSelectionLocked || this.selectedStudentId || this.detectedStudentId) {
			return
		}
		if (this.status !== 'recording' && this.status !== 'paused') return

		const preview = this.transcriptPreview
		if (!preview) return
		if (!forceFuzzy && preview === this.lastMatchedTranscript) return

		const allowFuzzy = forceFuzzy || (this.unmatchedSyncCount + 1) % 4 === 0
		this.lastMatchedTranscript = preview

		const matchedId = matchStudentFromTranscript(preview, studentsOrIndex, {
			allowFuzzy,
		})
		if (!matchedId) {
			this.unmatchedSyncCount += 1
			return
		}

		const normalizedId = normalizeId(matchedId)
		if (!normalizedId) {
			this.unmatchedSyncCount += 1
			return
		}

		this.detectedStudentId = normalizedId
		this.unmatchedSyncCount = 0
	}

	shrinkWidget() {
		if (!this.shouldShowWidget) return
		this.widgetMode = 'compact'
	}

	expandWidget() {
		if (!this.shouldShowWidget) return
		this.widgetMode = 'expanded'
	}

	toggleWidgetMode() {
		if (!this.shouldShowWidget) return
		this.widgetMode = this.widgetMode === 'expanded' ? 'compact' : 'expanded'
	}

	pauseRecording() {
		return this._pauseRecording()
	}

	async _pauseRecording() {
		if (this.status !== 'recording') return

		try {
			await this._recorder?.pause()
			runInAction(() => {
				this.status = 'paused'
				this.pauseCount += 1
			})
		} catch (cause) {
			runInAction(() => {
				this.handleRecorderError({
					code: cause?.name || 'PauseFailed',
					message: cause?.message || 'Failed to pause recording',
				})
			})
		}
	}

	resumeRecording() {
		return this._resumeRecording()
	}

	async _resumeRecording() {
		if (this.status !== 'paused') return

		try {
			await this._recorder?.resume()
			runInAction(() => {
				this.status = 'recording'
			})
		} catch (cause) {
			runInAction(() => {
				this.handleRecorderError({
					code: cause?.name || 'ResumeFailed',
					message: cause?.message || 'Failed to resume recording',
				})
			})
		}
	}

	requestCancelRecording() {
		if (!this.shouldShowWidget) return
		this.isCancelConfirmOpen = true
	}

	closeCancelRecording() {
		this.isCancelConfirmOpen = false
	}

	confirmCancelRecording() {
		this.isCancelConfirmOpen = false
		this.resetSessionState()
	}

	requestStudentAssignment(mode = 'change') {
		this.assignStudentModalMode = mode
		this.isAssignStudentOpen = true
	}

	closeStudentAssignment() {
		this.isAssignStudentOpen = false
		this.assignStudentModalMode = null
	}

	confirmStudentAssignment(studentId) {
		const normalizedId = normalizeId(studentId)
		if (!normalizedId) return

		const modalMode = this.assignStudentModalMode
		this.assignStudent(normalizedId)
		this.closeStudentAssignment()

		if (modalMode === 'finish') {
			this.finishRecording()
		}
	}

	finishRecording() {
		return this._finishRecording()
	}

	async _finishRecording() {
		if (this.status !== 'paused' || this.isSubmittingSession) return
		if (!this.assignedStudentId) {
			this.requestStudentAssignment('finish')
			return
		}

		if (this.recordingSeconds < 1) {
			this.handleRecorderError({
				code: 'EmptyRecording',
				message: 'Recording is too short. Continue recording or cancel.',
			})
			return
		}

		const studentId = normalizeId(this.assignedStudentId)
		const capturedRecordingSeconds = this.recordingSeconds
		const capturedRecordingId = this.recordingId
		const capturedRecordedAt = this.recordedAt || new Date().toISOString()

		try {
			this.status = 'processing'
			this.sessionCreationStatus = SESSION_CREATION_STATUS.DRAINING_TRANSCRIPT
			this.setSessionCreationError(null)
			this.setTranscriptStatus(TRANSCRIPT_STATUS.DRAINING)
			this.setTranscriptError(null)

			if (this._recorder) {
				await this._recorder.stop()
			}

			if (this._transcriptProvider?.flush) {
				try {
					await this._transcriptProvider.flush({ timeoutMs: 120000 })
				} catch (cause) {
					runInAction(() => {
						this.setTranscriptError({
							code: cause?.name || 'TranscriptDrainTimeout',
							message: cause?.message || 'Transcription drain timed out',
						})
					})
				}
			}

			if (!this.sessionAudioBlob || this.sessionAudioBlob.size === 0) {
				runInAction(() => {
					this.handleRecorderError({
						code: 'EmptyRecording',
						message: 'Recording is empty. Continue recording or cancel.',
					})
					this._disposeRecorder({ suppressCallbacks: true })
					this._transcriptProvider?.dispose?.()
					this.setTranscriptStatus(TRANSCRIPT_STATUS.ERROR)
					this.status = 'paused'
					this.sessionCreationStatus = SESSION_CREATION_STATUS.IDLE
				})
				return
			}

			runInAction(() => {
				this.sessionAudioDurationMs = capturedRecordingSeconds * 1000
				this.activeNoteStudentId = studentId
				this.finalizeRecordingTranscript()
				this.setTranscriptStatus(TRANSCRIPT_STATUS.COMPLETE)
				this._disposeRecorder({ suppressCallbacks: true })

				this.liveTranscript = ''
				this.detectedStudentId = ''
				this.selectedStudentId = ''
				this.studentSelectionLocked = false
				this.isAssignStudentOpen = false
			})

			await this._persistSessionNote({
				studentId,
				recordingId: capturedRecordingId,
				recordedAt: capturedRecordedAt,
				durationMs: this.sessionAudioDurationMs,
			})

			runInAction(() => {
				this._transcriptProvider?.dispose?.()
			})
		} catch (cause) {
			if (cause?.name === 'AbortError') return

			runInAction(() => {
				this.setTranscriptStatus(TRANSCRIPT_STATUS.ERROR)
				this.sessionCreationStatus = SESSION_CREATION_STATUS.FAILED
				this.setSessionCreationError({
					code: cause?.name || cause?.code || 'FinishFailed',
					message: cause?.message || 'Failed to finish recording',
					step: cause?.step,
				})
			})
		}
	}

	async _persistSessionNote({ studentId, recordingId, recordedAt, durationMs }) {
		this._abortSessionCreation()
		this._sessionCreationAbortController = new AbortController()
		const signal = this._sessionCreationAbortController.signal

		const payload = buildSessionNotePayload({
			recordingId,
			studentId,
			recordedAt,
			durationMs,
			segments: this.recordingTranscriptSegments,
		})

		let noteId = normalizeId(this.sessionNoteId)

		if (!noteId) {
			this.sessionCreationStatus = SESSION_CREATION_STATUS.CREATING_NOTE
			try {
				const created = await createSessionNote(payload, { signal })
				noteId = normalizeId(created?.id)
				if (!noteId) {
					const error = new Error('Session note creation returned no id')
					error.name = 'SessionCreationError'
					error.step = 'creating_note'
					throw error
				}
				runInAction(() => {
					this.sessionNoteId = noteId
				})
			} catch (cause) {
				if (cause?.name === 'AbortError') throw cause
				const error = new Error(cause?.message || 'Failed to create session note')
				error.name = cause?.name || 'SessionCreationError'
				error.step = 'creating_note'
				throw error
			}
		}

		runInAction(() => {
			this.sessionCreationStatus = SESSION_CREATION_STATUS.UPLOADING_AUDIO
		})
		try {
			await uploadSessionAudio(
				noteId,
				this.sessionAudioBlob,
				this.sessionAudioMimeType,
				{ signal },
			)
		} catch (cause) {
			if (cause?.name === 'AbortError') throw cause
			const error = new Error(cause?.message || 'Failed to upload session audio')
			error.name = cause?.name || 'AudioUploadError'
			error.step = 'uploading_audio'
			throw error
		}

		runInAction(() => {
			this.sessionAudioBlob = null
			this.sessionCreationStatus = SESSION_CREATION_STATUS.COMPLETED
			this.setSessionCreationError(null)
			this.status = 'processing'
			this.beginAiProcessingPoll()
		})
	}

	retrySessionCreation() {
		if (
			this.sessionCreationStatus !== SESSION_CREATION_STATUS.FAILED ||
			this.isSubmittingSession
		) {
			return
		}

		if (!this.sessionAudioBlob || this.sessionAudioBlob.size === 0) {
			this.setSessionCreationError({
				code: 'MissingAudio',
				message: 'Recording audio is no longer available. Please record again.',
			})
			return
		}

		const studentId = normalizeId(this.activeNoteStudentId)
		if (!studentId) {
			this.status = 'paused'
			this.sessionCreationStatus = SESSION_CREATION_STATUS.IDLE
			this.requestStudentAssignment('finish')
			return
		}

		this.setSessionCreationError(null)
		this.status = 'processing'

		return this._persistSessionNote({
			studentId,
			recordingId: this.recordingId,
			recordedAt: this.recordedAt || new Date().toISOString(),
			durationMs: this.sessionAudioDurationMs || this.recordingSeconds * 1000,
		}).catch(cause => {
			if (cause?.name === 'AbortError') return
			runInAction(() => {
				this.sessionCreationStatus = SESSION_CREATION_STATUS.FAILED
				this.setSessionCreationError({
					code: cause?.name || cause?.code || 'SessionCreationError',
					message: cause?.message || 'Failed to save session',
					step: cause?.step,
				})
			})
		})
	}

	discardFailedSession() {
		const noteId = normalizeId(this.sessionNoteId)
		if (noteId) {
			deleteSessionNote(noteId).catch(() => {})
		}
		this.resetSessionState()
	}

	completeProcessing() {
		if (this.status !== 'processing') return

		const studentId = normalizeId(this.activeNoteStudentId || this.assignedStudentId)
		if (!studentId) {
			this.status = 'paused'
			this.requestStudentAssignment('finish')
			return
		}

		if (this.activeNoteRecord) {
			this.activeNoteRecord.studentId = studentId
			this.activeNoteRecord.durationLabel = formatDuration(this.recordingSeconds)
			this.activeNoteRecord.noteDurationLabel = this.noteDurationLabel
			this.activeNoteRecord.recordingSeconds = this.recordingSeconds
			this.activeNoteRecord.transcript =
				this.activeNoteRecord.transcript || this.finalTranscriptPreview
			this.activeNoteRecord.pauseCount = this.pauseCount

			const existingNotes = this.notesByStudentId.get(studentId) || []
			if (!existingNotes.some(note => note.id === this.activeNoteRecord.id)) {
				this.notesByStudentId.set(studentId, [
					this.activeNoteRecord,
					...existingNotes,
				])
			}
		}

		this.status = 'idle'
		this.isDrawerOpen = true
		this.sessionDrawerStatus = SESSION_DRAWER_STATUS.READY
		this.showAddedToast = true
		this.activeNoteStudentId = studentId
		this.kodaPanel = null
	}

	_clearActionItemDebounceTimers() {
		Object.values(this._actionItemDebounceTimers).forEach(timerId => {
			window.clearTimeout(timerId)
		})
		this._actionItemDebounceTimers = {}
	}

	_getSavedPersistedItem(itemId) {
		return this._persistedActionItemsSaved.find(item => item.id === itemId) || null
	}

	_setSavedPersistedItems(items = []) {
		this._persistedActionItemsSaved = items.map(item => clonePersistedActionItem(item))
	}

	_applyPersistedItemFromApi(item) {
		const mapped = mapActionItemFromApi(item, { saveStatus: 'saved' })
		const index = this.actionItemsPersisted.findIndex(row => row.id === mapped.id)
		if (index >= 0) {
			this.actionItemsPersisted[index] = mapped
		} else {
			this.actionItemsPersisted.push(mapped)
		}

		const savedIndex = this._persistedActionItemsSaved.findIndex(row => row.id === mapped.id)
		const savedCopy = clonePersistedActionItem(mapped)
		if (savedIndex >= 0) {
			this._persistedActionItemsSaved[savedIndex] = savedCopy
		} else {
			this._persistedActionItemsSaved.push(savedCopy)
		}

		if (this.sessionNote) {
			this.sessionNote = {
				...this.sessionNote,
				actionItems: this.actionItemsPersisted.map(row => ({
					id: row.id,
					text: row.text,
					ownerType: mapOwnerTypeToApi(row.ownerType),
					ownerLabel: row.ownerLabel,
					status: row.status,
					sortOrder: row.sortOrder,
					source: row.source,
				})),
			}
		}

		this._syncActiveNoteRecordFromSessionDrawer()
	}

	_syncActiveNoteRecordFromSessionDrawer() {
		if (!this.activeNoteRecord) return

		this.activeNoteRecord.generalNotes = this.summarySaved
			? [
					{
						id: 'general-summary',
						text: this.summarySaved,
						type: 'note',
						kodaState: 'idle',
					},
			  ]
			: []
		this.activeNoteRecord.actionItems = this.actionItemsPersisted.map(item => ({
			id: item.id,
			text: item.text,
			kodaState: item.status === 'done' ? 'done' : 'idle',
			ownerType: item.ownerType,
		}))
		if (this.sessionNote?.title) {
			this.activeNoteRecord.title = this.sessionNote.title
		}
	}

	hydrateSessionDrawer(sessionNote, context = {}) {
		const noteId = normalizeId(sessionNote?.id)
		if (!noteId) return

		this.activeSessionNoteId = noteId
		this.sessionNote = sessionNote
		this.sessionDrawerStatus = SESSION_DRAWER_STATUS.READY
		this.sessionDrawerError = null
		this.sessionDrawerConflict = false
		this.summaryMode = SUMMARY_MODE.VIEW
		this.summarySaved = String(sessionNote?.summary || '')
		this.summaryDraft = this.summarySaved
		this.summarySaveStatus = 'idle'
		this.summarySaveError = null

		const items = (sessionNote?.actionItems || []).map(item =>
			mapActionItemFromApi(item, { saveStatus: 'idle' }),
		)
		this.actionItemsPersisted = items
		this._setSavedPersistedItems(items)
		this.actionItemDraft = null
		this.isTranscriptDrawerOpen = false
		this.transcriptDrawerStatus = TRANSCRIPT_DRAWER_STATUS.CLOSED
		this.transcriptDrawerError = null

		const studentId = normalizeId(context.studentId || sessionNote?.studentId)
		if (studentId) {
			this.activeNoteStudentId = studentId
		}

		if (context.transcriptText) {
			this.transcriptCache = {
				sessionNoteId: noteId,
				text: String(context.transcriptText),
				segments: [],
			}
		}
	}

	_shouldRegenerateSessionAi(note) {
		if (!note) return false

		const hasSummary = Boolean(String(note.summary || '').trim())
		const hasActions =
			Array.isArray(note.actionItems) && note.actionItems.length > 0
		const aiFailed =
			note.status === 'failed' || note?.ai?.status === 'failed'

		return aiFailed || (!hasSummary && !hasActions)
	}

	async _waitForAiJobCompletion(noteId, { timeoutMs = 120000, pollMs = 2000 } = {}) {
		const normalizedId = normalizeId(noteId)
		if (!normalizedId) return null

		const startedAt = Date.now()

		while (Date.now() - startedAt < timeoutMs) {
			const job = await getLatestAiJob(normalizedId)
			const status = String(job?.status || '')

			if (status === 'completed' || status === 'failed') {
				return job
			}

			await new Promise(resolve => window.setTimeout(resolve, pollMs))
		}

		return null
	}

	async _regenerateSessionAiIfNeeded(noteId, note) {
		if (!this._shouldRegenerateSessionAi(note)) {
			return note
		}

		let transcript
		try {
			transcript = await getSessionTranscript(noteId)
		} catch {
			return note
		}

		const transcriptText = buildFullTranscriptText(transcript)
		if (!transcriptText) {
			return note
		}

		this.transcriptCache = {
			sessionNoteId: noteId,
			text: transcriptText,
			segments: transcript?.transcriptSegments || [],
		}

		let latestJob
		try {
			latestJob = await getLatestAiJob(noteId)
		} catch {
			return note
		}

		if (String(latestJob?.status || '') === 'failed') {
			await retryAiJob(noteId, latestJob.jobId)
			await this._waitForAiJobCompletion(noteId)
		} else if (
			['queued', 'processing'].includes(String(latestJob?.status || ''))
		) {
			await this._waitForAiJobCompletion(noteId)
		}

		try {
			return await getSessionNote(noteId)
		} catch {
			return note
		}
	}

	async openSessionDrawer(sessionNoteId) {
		const noteId = normalizeId(sessionNoteId)
		if (!noteId) return

		this.sessionDrawerStatus = SESSION_DRAWER_STATUS.LOADING
		this.sessionDrawerError = null
		this.isDrawerOpen = true
		this.showAddedToast = false
		this.kodaPanel = null

		try {
			let note = await getSessionNote(noteId)
			note = await this._regenerateSessionAiIfNeeded(noteId, note)
			this.hydrateSessionDrawer(note, { studentId: this.activeNoteStudentId })
			this.activeNoteRecord = mapSessionNoteToDrawerRecord(note, {
				recordingSeconds: Math.round((note.durationMs || 0) / 1000),
				studentId: this.activeNoteStudentId || note.studentId,
				transcriptText: this.transcriptCache?.text || '',
			})
		} catch (cause) {
			this.sessionDrawerStatus = SESSION_DRAWER_STATUS.ERROR
			this.sessionDrawerError = {
				code: cause?.name || 'SessionDrawerError',
				message: cause?.message || 'Could not load session',
			}
		}
	}

	retryOpenSessionDrawer() {
		const noteId = normalizeId(this.activeSessionNoteId)
		if (!noteId) return
		return this.openSessionDrawer(noteId)
	}

	resetSessionDrawerState() {
		this._clearActionItemDebounceTimers()
		this.sessionDrawerStatus = SESSION_DRAWER_STATUS.IDLE
		this.sessionDrawerError = null
		this.activeSessionNoteId = ''
		this.sessionNote = null
		this.summaryMode = SUMMARY_MODE.VIEW
		this.summaryDraft = ''
		this.summarySaved = ''
		this.summarySaveStatus = 'idle'
		this.summarySaveError = null
		this.actionItemsPersisted = []
		this._persistedActionItemsSaved = []
		this.actionItemDraft = null
		this.isTranscriptDrawerOpen = false
		this.transcriptDrawerStatus = TRANSCRIPT_DRAWER_STATUS.CLOSED
		this.transcriptDrawerError = null
		this.transcriptCache = null
		this.sessionDrawerConflict = false
		this.isDiscardSummaryPromptOpen = false
	}

	beginSummaryEdit() {
		if (this.summaryMode === SUMMARY_MODE.EDIT) return
		this.summaryDraft = this.summarySaved
		this.summaryMode = SUMMARY_MODE.EDIT
		this.summarySaveStatus = 'idle'
		this.summarySaveError = null
	}

	cancelSummaryEdit() {
		this.summaryDraft = this.summarySaved
		this.summaryMode = SUMMARY_MODE.VIEW
		this.summarySaveStatus = 'idle'
		this.summarySaveError = null
	}

	setSummaryDraft(value) {
		this.summaryDraft = String(value ?? '')
	}

	async saveSummary() {
		const noteId = normalizeId(this.activeSessionNoteId)
		if (!noteId || this.summarySaveStatus === 'saving') return

		this.summarySaveStatus = 'saving'
		this.summarySaveError = null

		try {
			const updated = await updateSessionSummary(noteId, {
				summary: this.summaryDraft,
				updatedAt: this.sessionNote?.updatedAt,
			})
			this.summarySaved = String(updated?.summary ?? this.summaryDraft)
			this.summaryDraft = this.summarySaved
			this.summaryMode = SUMMARY_MODE.VIEW
			this.summarySaveStatus = 'idle'
			if (this.sessionNote) {
				this.sessionNote = {
					...this.sessionNote,
					summary: this.summarySaved,
					updatedAt: updated?.updatedAt || this.sessionNote.updatedAt,
				}
			}
			this._syncActiveNoteRecordFromSessionDrawer()
		} catch (cause) {
			this.summarySaveStatus = 'error'
			this.summarySaveError = {
				code: cause?.name || 'SummarySaveError',
				message: cause?.message || 'Failed to save summary',
			}
			if (cause?.name === 'ConflictError') {
				this.sessionDrawerConflict = true
			}
		}
	}

	startActionItemDraft() {
		if (this.actionItemDraft) return
		this.actionItemDraft = {
			clientDraftId: `draft-${Date.now()}`,
			text: '',
			ownerType: 'you',
			ownerLabel: 'You',
			saveStatus: 'editing',
			error: null,
		}
	}

	updateActionItemDraft(patch = {}) {
		if (!this.actionItemDraft) return
		this.actionItemDraft = {
			...this.actionItemDraft,
			...patch,
		}
	}

	cancelActionItemDraft() {
		this.actionItemDraft = null
	}

	async saveActionItemDraft(studentName = '') {
		const noteId = normalizeId(this.activeSessionNoteId)
		const draft = this.actionItemDraft
		if (!noteId || !draft || draft.saveStatus === 'saving') return

		const payload = buildActionItemCreatePayload(draft, { studentName })
		if (!payload.text) return

		this.actionItemDraft = {
			...draft,
			saveStatus: 'saving',
			error: null,
		}

		try {
			const created = await createSessionActionItem(noteId, {
				...payload,
				sortOrder: this.actionItemsPersisted.length,
			})
			this._applyPersistedItemFromApi(created)
			this.actionItemDraft = null
			if (this.sessionNote?.updatedAt) {
				this.sessionNote.updatedAt = new Date().toISOString()
			}
		} catch (cause) {
			this.actionItemDraft = {
				...draft,
				saveStatus: 'error',
				error: {
					code: cause?.name || 'ActionItemCreateError',
					message: cause?.message || 'Failed to save action item',
				},
			}
		}
	}

	_schedulePersistedActionItemPatch(itemId) {
		if (this._actionItemDebounceTimers[itemId]) {
			window.clearTimeout(this._actionItemDebounceTimers[itemId])
		}

		this._actionItemDebounceTimers[itemId] = window.setTimeout(() => {
			delete this._actionItemDebounceTimers[itemId]
			this.flushPersistedActionItem(itemId)
		}, ACTION_ITEM_DEBOUNCE_MS)
	}

	updatePersistedActionItemText(itemId, text) {
		const item = this.actionItemsPersisted.find(row => row.id === itemId)
		if (!item) return
		item.text = String(text ?? '')
		item.saveStatus = 'idle'
		item.saveError = null
		this._schedulePersistedActionItemPatch(itemId)
	}

	async flushPersistedActionItem(itemId) {
		const noteId = normalizeId(this.activeSessionNoteId)
		const draft = this.actionItemsPersisted.find(row => row.id === itemId)
		const saved = this._getSavedPersistedItem(itemId)
		if (!noteId || !draft || !saved) return

		const patch = {}
		if (draft.text !== saved.text) patch.text = draft.text
		if (draft.ownerType !== saved.ownerType) {
			patch.ownerType = mapOwnerTypeToApi(draft.ownerType)
			patch.ownerLabel = draft.ownerLabel
		}
		if (draft.status !== saved.status) {
			patch.status = draft.status
		}

		if (Object.keys(patch).length === 0) return

		draft.saveStatus = 'saving'
		draft.saveError = null

		try {
			const updated = await updateSessionActionItem(noteId, itemId, patch)
			this._applyPersistedItemFromApi(updated)
		} catch (cause) {
			draft.saveStatus = 'error'
			draft.saveError = {
				code: cause?.name || 'ActionItemSaveError',
				message: cause?.message || 'Failed to save action item',
			}
			if (cause?.name === 'ConflictError') {
				this.sessionDrawerConflict = true
			}
		}
	}

	async flushAllPersistedActionItems() {
		this._clearActionItemDebounceTimers()
		const ids = this.actionItemsPersisted.map(item => item.id)
		for (const itemId of ids) {
			await this.flushPersistedActionItem(itemId)
		}
	}

	async toggleActionItemDone(itemId) {
		const item = this.actionItemsPersisted.find(row => row.id === itemId)
		if (!item) return

		item.status = item.status === 'done' ? 'open' : 'done'
		item.saveStatus = 'idle'
		await this.flushPersistedActionItem(itemId)
	}

	async updatePersistedActionItemOwner(itemId, ownerType, ownerLabel) {
		const item = this.actionItemsPersisted.find(row => row.id === itemId)
		if (!item) return

		item.ownerType = ownerType
		item.ownerLabel = ownerLabel
		item.saveStatus = 'idle'
		await this.flushPersistedActionItem(itemId)
	}

	async deleteActionItem(itemId) {
		const noteId = normalizeId(this.activeSessionNoteId)
		if (!noteId) return

		const item = this.actionItemsPersisted.find(row => row.id === itemId)
		if (!item) return

		item.saveStatus = 'saving'
		try {
			await deleteSessionActionItem(noteId, itemId)
			this.actionItemsPersisted = this.actionItemsPersisted.filter(row => row.id !== itemId)
			this._persistedActionItemsSaved = this._persistedActionItemsSaved.filter(
				row => row.id !== itemId,
			)
			if (this.sessionNote) {
				this.sessionNote = {
					...this.sessionNote,
					actionItems: (this.sessionNote.actionItems || []).filter(row => row.id !== itemId),
				}
			}
			this._syncActiveNoteRecordFromSessionDrawer()
		} catch (cause) {
			item.saveStatus = 'error'
			item.saveError = {
				code: cause?.name || 'ActionItemDeleteError',
				message: cause?.message || 'Failed to delete action item',
			}
		}
	}

	async reloadSessionDrawerFromServer() {
		const noteId = normalizeId(this.activeSessionNoteId)
		if (!noteId) return

		this.sessionDrawerConflict = false
		this.summaryMode = SUMMARY_MODE.VIEW
		this.cancelActionItemDraft()

		try {
			const note = await getSessionNote(noteId)
			this.hydrateSessionDrawer(note, { studentId: this.activeNoteStudentId })
			this.activeNoteRecord = mapSessionNoteToDrawerRecord(note, {
				recordingSeconds: Math.round((note.durationMs || 0) / 1000),
				studentId: this.activeNoteStudentId || note.studentId,
				transcriptText: this.transcriptCache?.text || '',
			})
		} catch (cause) {
			this.sessionDrawerError = {
				code: cause?.name || 'SessionDrawerError',
				message: cause?.message || 'Could not reload session',
			}
		}
	}

	openTranscriptDrawer() {
		const noteId = normalizeId(this.activeSessionNoteId)
		if (!noteId) return

		this.isTranscriptDrawerOpen = true

		if (
			this.transcriptCache?.sessionNoteId === noteId &&
			(this.transcriptCache?.segments?.length > 0 || this.transcriptCache?.text)
		) {
			this.transcriptDrawerStatus = TRANSCRIPT_DRAWER_STATUS.READY
			this.transcriptDrawerError = null
			return
		}

		this.transcriptDrawerStatus = TRANSCRIPT_DRAWER_STATUS.LOADING
		this.transcriptDrawerError = null

		return getSessionTranscript(noteId)
			.then(transcript => {
				if (normalizeId(this.activeSessionNoteId) !== noteId) return
				const text = buildFullTranscriptText(transcript)
				this.transcriptCache = {
					sessionNoteId: noteId,
					text,
					segments: transcript?.transcriptSegments || [],
				}
				this.transcriptDrawerStatus = TRANSCRIPT_DRAWER_STATUS.READY
			})
			.catch(cause => {
				if (normalizeId(this.activeSessionNoteId) !== noteId) return
				this.transcriptDrawerStatus = TRANSCRIPT_DRAWER_STATUS.ERROR
				this.transcriptDrawerError = {
					code: cause?.name || 'TranscriptLoadError',
					message: cause?.message || 'Could not load transcript',
				}
			})
	}

	closeTranscriptDrawer() {
		this.isTranscriptDrawerOpen = false
		this.transcriptDrawerStatus = TRANSCRIPT_DRAWER_STATUS.CLOSED
		this.transcriptDrawerError = null
	}

	toggleTranscriptDrawer() {
		if (this.isTranscriptDrawerOpen) {
			this.closeTranscriptDrawer()
			return
		}
		this.openTranscriptDrawer()
	}

	retryTranscriptDrawer() {
		const noteId = normalizeId(this.activeSessionNoteId)
		if (!noteId) return
		if (this.transcriptCache?.sessionNoteId === noteId) {
			this.transcriptCache = null
		}
		return this.openTranscriptDrawer()
	}

	requestCloseDrawer() {
		if (this.hasUnsavedSummary) {
			this.isDiscardSummaryPromptOpen = true
			return false
		}
		void this._finalizeCloseDrawer()
		return true
	}

	confirmDiscardSummaryAndClose() {
		this.isDiscardSummaryPromptOpen = false
		this.cancelSummaryEdit()
		this._finalizeCloseDrawer()
	}

	cancelDiscardSummaryPrompt() {
		this.isDiscardSummaryPromptOpen = false
	}

	async _finalizeCloseDrawer() {
		this.cancelActionItemDraft()
		await this.flushAllPersistedActionItems()
		this.closeTranscriptDrawer()
		this.isDrawerOpen = false
		this.kodaPanel = null
		this.showAddedToast = false
		this.resetSessionDrawerState()
	}

	closeDrawer() {
		this.requestCloseDrawer()
	}

	dismissAddedToast() {
		this.showAddedToast = false
	}

	openStoredNote(studentId, noteId) {
		const normalizedStudentId = normalizeId(studentId)
		this.activeNoteStudentId = normalizedStudentId
		this.showAddedToast = false
		this.kodaPanel = null
		this.openSessionDrawer(noteId)
	}

	setNewGeneralNoteText(value) {
		this.newGeneralNoteText = value
	}

	setNewActionItemText(value) {
		this.newActionItemText = value
	}

	addGeneralNote() {
		const text = String(this.newGeneralNoteText || '').trim()
		if (!text || !this.activeNoteRecord) return
		this.activeNoteRecord.generalNotes.push({
			id: createItemId('general'),
			text,
			type: 'note',
			kodaState: 'idle',
		})
		this.newGeneralNoteText = ''
	}

	addActionItem() {
		const text = String(this.newActionItemText || '').trim()
		if (!text || !this.activeNoteRecord) return
		this.activeNoteRecord.actionItems.push({
			id: createItemId('action'),
			text,
			kodaState: 'idle',
			ownerType: 'you',
		})
		this.newActionItemText = ''
	}

	addEmptyActionItem() {
		if (!this.activeNoteRecord) return
		this.activeNoteRecord.actionItems.push({
			id: createItemId('action'),
			text: 'New action item',
			kodaState: 'idle',
			ownerType: 'you',
		})
	}

	updateGeneralNote(noteId, value) {
		const target = this.currentGeneralNotes.find(note => note.id === noteId)
		if (!target) return
		target.text = value
	}

	updateActionItem(itemId, value) {
		const target = this.currentActionItems.find(item => item.id === itemId)
		if (!target) return
		target.text = value
	}

	openKodaPanel(section, itemId, mode) {
		const target = this.findActiveItem(section, itemId)
		if (!target) return
		this.kodaPanel = {
			section,
			itemId,
			mode,
			title: this.buildKodaPrompt(target, mode),
			sourceText: target.text,
			status: 'draft',
		}
	}

	setKodaPrompt(value) {
		if (!this.kodaPanel) return
		this.kodaPanel = {
			...this.kodaPanel,
			title: value,
		}
	}

	confirmKodaPanel() {
		if (!this.kodaPanel) return
		const target = this.findActiveItem(
			this.kodaPanel.section,
			this.kodaPanel.itemId,
		)
		if (target) target.kodaState = 'working'
		this.kodaPanel = {
			...this.kodaPanel,
			status: 'working',
		}
	}

	completeKodaPanel() {
		if (!this.kodaPanel) return
		const target = this.findActiveItem(
			this.kodaPanel.section,
			this.kodaPanel.itemId,
		)
		if (target) {
			target.kodaState = 'done'
		}
		this.kodaPanel = this.kodaPanel
			? {
					...this.kodaPanel,
					status: 'done',
			  }
			: null
	}

	closeKodaPanel() {
		if (this.kodaPanel?.status === 'working') {
			const target = this.findActiveItem(
				this.kodaPanel.section,
				this.kodaPanel.itemId,
			)
			if (target) {
				target.kodaState = 'idle'
			}
		}
		this.kodaPanel = null
	}

	getNotesForStudent(studentId) {
		return this.notesByStudentId.get(normalizeId(studentId)) || []
	}

	getSessionNotesListState(studentId) {
		const normalizedId = normalizeId(studentId)
		return (
			this.sessionNotesListByStudentId.get(normalizedId) || {
				status: 'idle',
				notes: [],
				total: 0,
				error: null,
			}
		)
	}

	async loadSessionNotesForStudent(studentId) {
		const normalizedId = normalizeId(studentId)
		if (!normalizedId) return

		const current = this.getSessionNotesListState(normalizedId)
		this.sessionNotesListByStudentId.set(normalizedId, {
			...current,
			status: 'loading',
			error: null,
		})

		try {
			const data = await listSessionNotesForStudent(normalizedId)
			runInAction(() => {
				this.sessionNotesListByStudentId.set(normalizedId, {
					status: 'ready',
					notes: Array.isArray(data?.notes) ? data.notes : [],
					total: Number(data?.total) || 0,
					error: null,
				})
			})
		} catch (cause) {
			runInAction(() => {
				this.sessionNotesListByStudentId.set(normalizedId, {
					status: 'error',
					notes: [],
					total: 0,
					error: {
						code: cause?.name || 'SessionNotesListError',
						message: cause?.message || 'Could not load session notes',
					},
				})
			})
		}
	}

	async deleteSessionNoteFromList(studentId, noteId) {
		const normalizedStudentId = normalizeId(studentId)
		const normalizedNoteId = normalizeId(noteId)
		if (!normalizedStudentId || !normalizedNoteId) return

		const current = this.getSessionNotesListState(normalizedStudentId)
		const previousNotes = current.notes
		const optimisticNotes = previousNotes.filter(note => note.id !== normalizedNoteId)

		this.sessionNotesListByStudentId.set(normalizedStudentId, {
			...current,
			notes: optimisticNotes,
			total: optimisticNotes.length,
		})

		try {
			await deleteSessionNote(normalizedNoteId)
			await this.loadSessionNotesForStudent(normalizedStudentId)
		} catch (cause) {
			runInAction(() => {
				this.sessionNotesListByStudentId.set(normalizedStudentId, {
					...current,
					status: 'error',
					error: {
						code: cause?.name || 'SessionNoteDeleteError',
						message: cause?.message || 'Could not delete session note',
					},
				})
			})
			throw cause
		}
	}

	async startRecordingForStudent(studentId) {
		const normalizedId = normalizeId(studentId)
		if (!normalizedId) return

		await this.startRecording()
		this.assignStudent(normalizedId)
	}

	resetSessionState() {
		this._abortSessionCreation()
		this.clearAiProcessingState()
		this._transcriptProvider?.dispose?.()
		this._disposeRecorder({ suppressCallbacks: true })
		this.status = 'idle'
		this.widgetMode = 'expanded'
		this.recordingSeconds = 0
		this.pauseCount = 0
		this.detectedStudentId = ''
		this.selectedStudentId = ''
		this.studentSelectionLocked = false
		this.lastMatchedTranscript = ''
		this.unmatchedSyncCount = 0
		this.assignStudentModalMode = null
		this.activeNoteRecord = null
		this.activeNoteStudentId = ''
		this.isAssignStudentOpen = false
		this.isCancelConfirmOpen = false
		this.isDrawerOpen = false
		this.showAddedToast = false
		this.kodaPanel = null
		this.resetSessionDrawerState()
		this.newGeneralNoteText = ''
		this.newActionItemText = ''
		this.recorderError = null
		this.recordedAt = ''
		this.clearSessionCapture()
		this.clearTranscript()
		this.recordingId = ''
		this.recordingTranscriptSegments = []
		this.sessionCreationStatus = SESSION_CREATION_STATUS.IDLE
		this.sessionNoteId = ''
		this.sessionCreationError = null
	}

	forceCleanupRecording() {
		this.resetSessionState()
	}

	buildNoteRecord() {
		return {
			id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
			title: 'Session 4: Country Finder Review',
			createdAt: new Date().toISOString(),
			durationLabel: formatDuration(this.recordingSeconds),
			noteDurationLabel: this.noteDurationLabel,
			recordingSeconds: this.recordingSeconds,
			pauseCount: this.pauseCount,
			transcript: this.transcriptPreview,
			topic: 'Country Finder',
			keyRecommendation:
				'Prepare and share a clear cost comparison between UCLA and UCSD.',
			generalNotes: cloneItems(DEFAULT_GENERAL_NOTES()),
			actionItems: cloneItems(DEFAULT_ACTION_ITEMS()),
			speakerShares: [
				{ id: 'counsellor', label: 'You', percent: 62 },
				{ id: 'student', label: 'Sarah Chen', percent: 38 },
			],
		}
	}

	buildKodaPrompt(target, mode) {
		if (mode === 'remind') {
			return `Create a reminder for the student: ${target.text}`
		}
		return `Help me complete this action item from the session note: ${target.text}`
	}

	findActiveItem(section, itemId) {
		if (!this.activeNoteRecord) return null
		const list =
			section === 'general'
				? this.activeNoteRecord.generalNotes
				: this.activeNoteRecord.actionItems
		return list.find(item => item.id === itemId) || null
	}
}

export const noteTakerStore = new NoteTakerStore()
